/* eslint-disable consistent-return */
import React, { cloneElement, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useLocation, useMatch } from "react-router-dom";
import { ESSENTIAL_TOURS } from "./tours";
import { isNonEmptyArray } from "../../utils";
import { isSegFeatureEnabled, SegFlags, translate } from "../../providers";
import {
	setActiveTour,
	setContextProductTours,
	setContextTourExploration,
	setFirstTimeGuiding,
	setOpenProductTourMenu,
	setOpenProductTourWarning,
	setPlayableTours,
	setStartingProductTourDocumentId,
} from "../../slice";
import { getActivePlayableTours } from "./utils/utils";
import { ProductTourMenuDialog } from ".";
import { useApi, useEffectOnce } from "../../hooks";
import { ProjectDocumentService, ProductTourManagementService } from "../../../api";
import { CustomDialog } from "../dialogs";
import { NavUtils } from "../../../navigation";

export default function ProductTours() {
	const segFlags = useSelector(({ context }) => context.segFlags);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const companyId = useSelector(({ context }) => context.company?.id);
	const activeTour = useSelector(({ context }) => context?.activeTour);
	const savedTours = useSelector(({ context }) => context.tours);
	const playableTours = useSelector(({ context }) => context.playableTours);
	const firstTimeGuiding = useSelector(({ context }) => context.firstTimeGuiding);
	const openProductTourMenu = useSelector(({ context }) => context.openProductTourMenu);
	const openProductTourWarning = useSelector(({ context }) => context.openProductTourWarning);
	const checkedPlayableTours = useSelector(({ context }) => context.checkedPlayableTours);
	const startingProductTourDocumentId = useSelector(({ context }) => context.startingProductTourDocumentId);
	const { call: getCompanyProductTours } = useApi(ProductTourManagementService.getCompanyProductTours);
	const { call: getCompletedDocument } = useApi(ProjectDocumentService.getCompletedDocument);
	const intervalProductTourRef = useRef(null);
	const location = useLocation();
	const dispatch = useDispatch();
	const matchDocCenter = useMatch("/project/:projectId/documents/:documentId");
	const currentPageAvailableTours = useMemo(
		() =>
			Object.values(ESSENTIAL_TOURS).filter(
				(tour) =>
					(!isNonEmptyArray(tour.flags) || (isSegFeatureEnabled(tour.flags) && segFlags)) &&
					matchPath({ path: tour.locations?.[0] }, location.pathname)
			),
		[location, segFlags]
	);
	const currentTour = useMemo(
		() => activeTour && currentPageAvailableTours.find((tour) => tour.id === activeTour),
		[activeTour, currentPageAvailableTours]
	);
	const hasPlayableTours = useMemo(
		() => (getActivePlayableTours(projectId, ESSENTIAL_TOURS, playableTours) || []).length > 0,
		[projectId, playableTours]
	);
	const saveTours = useCallback(
		(tours) => {
			dispatch(setContextProductTours(tours));
		},
		[dispatch]
	);
	const hasPTRights = isSegFeatureEnabled(SegFlags.PRODUCT_TOUR);
	useEffect(() => {
		/** First retrieval of tours */
		if (!checkedPlayableTours && companyId && hasPTRights) {
			getCompanyProductTours({ companyId })
				.then((data) => dispatch(setPlayableTours(data)))
				.catch(console.error);
		}
	}, [checkedPlayableTours, companyId, dispatch, getCompanyProductTours, hasPTRights]);
	useEffectOnce(
		() => {
			/** Compare tours against local storage and get local trigger */
			saveTours(null);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[dispatch, projectId, saveTours],
		() => projectId
	);
	const handleCompleteTour = useCallback(
		(tours) => {
			// eslint-disable-next-line prefer-const
			let newTours = { ...savedTours };
			tours.forEach((tour) => {
				const { id, flags, start } = tour;
				const idToPlace = start || id;
				if (!flags) {
					newTours[0] = [...newTours[0], idToPlace];
				} else if (Array.isArray(newTours?.[1]) && !newTours[1].some((tourId) => tourId === idToPlace)) {
					newTours[1] = [...newTours[1], idToPlace];
				} else if (!newTours?.[1]) {
					newTours = { ...newTours, ...{ 1: [idToPlace] } };
				}
			});
			saveTours(newTours);
			dispatch(setActiveTour(null));
			if (firstTimeGuiding) {
				dispatch(setFirstTimeGuiding(false));
			}
		},
		[dispatch, saveTours, savedTours, firstTimeGuiding]
	);
	const handleCloseTour = useCallback(() => {
		dispatch(setActiveTour(null));
	}, [dispatch]);
	useEffect(() => {
		/** Checks if there is a completed document every 5 minutes
		 * retrieves document id.
		 * Important for most product tours that require to have documents.
		 * Launched at start when inside project.
		 */
		const clearPolling = () => {
			if (intervalProductTourRef.current) {
				clearInterval(intervalProductTourRef.current);
				intervalProductTourRef.current = null;
			}
		};
		let isActive = true;
		const setupPolling = async () => {
			try {
				if (
					!projectId ||
					startingProductTourDocumentId ||
					!isSegFeatureEnabled([SegFlags.DOCUMENT_CENTER, SegFlags.PRODUCT_TOUR])
				) {
					return;
				}
				const poll = async () => {
					try {
						const data = await getCompletedDocument({ projectId });
						if (data?.value) {
							dispatch(setStartingProductTourDocumentId(data.value));
							clearPolling();
						}
					} catch (err) {
						console.error("Polling error:", err);
					}
				};
				await poll();
				if (isActive) {
					intervalProductTourRef.current = setInterval(poll, 30000);
				}
			} catch (err) {
				console.error("useEffect setup error:", err);
				clearPolling();
			}
		};
		setupPolling();
		return () => {
			isActive = false;
			clearPolling();
		};
	}, [dispatch, getCompletedDocument, projectId, startingProductTourDocumentId]);
	const handleCloseWarning = () => {
		dispatch(setOpenProductTourWarning(false));
	};
	const handleCloseProductTourMenu = () => {
		dispatch(setOpenProductTourMenu(false));
		if (firstTimeGuiding) {
			dispatch(setFirstTimeGuiding(false));
		}
	};
	const handleRedirection = () => {
		if (!matchDocCenter) {
			window.location.href = NavUtils.goToDocumentCenter(projectId);
		}
		handleCloseWarning();
	};
	useEffect(() => {
		// A. Checks if first time visiting and opens by default or not tour menu
		if (projectId && hasPTRights && hasPlayableTours) {
			dispatch(setContextTourExploration(null));
		}
	}, [dispatch, projectId, hasPlayableTours, hasPTRights]);
	useEffect(() => {
		// B. Opens menu if all conditions fulfilled
		if (firstTimeGuiding && startingProductTourDocumentId && hasPTRights && hasPlayableTours) {
			dispatch(setOpenProductTourMenu(true));
		}
	}, [dispatch, startingProductTourDocumentId, firstTimeGuiding, hasPlayableTours, hasPTRights]);
	if (hasPTRights && hasPlayableTours) {
		if (!startingProductTourDocumentId) {
			return (
				<CustomDialog
					closeLabel={translate("common:btn.close")}
					open={openProductTourWarning}
					submitLabel={translate("common:product-tour.dialog.no-doc.warning.subtitle.import-close")}
					title={translate("common:product-tour.dialog.no-doc.warning.title")}
					onClose={handleCloseWarning}
					onSubmit={handleRedirection}
				>
					<div>{translate("common:product-tour.dialog.no-doc.warning.subtitle.1")}</div>
					<div>{translate("common:product-tour.dialog.no-doc.warning.subtitle.2")}</div>
					<div>{translate("common:product-tour.dialog.no-doc.warning.subtitle.3")}</div>
				</CustomDialog>
			);
		}
		if (activeTour && currentTour?.component) {
			const { component, ...tour } = currentTour;
			return cloneElement(currentTour.component, {
				activatedTour: activeTour,
				currentTour: tour,
				onClose: () => handleCloseTour([currentTour]),
				onCompleteTour: () => handleCompleteTour([currentTour]),
			});
		}
		return <ProductTourMenuDialog open={openProductTourMenu} onClose={handleCloseProductTourMenu} />;
	}
	return null;
}
